/**
 * Subscription plans — FALLBACK data and status constants for frontend display.
 *
 * This file contains pure data only:
 *   - PLAN_STATUS  — mirrors Stripe/backend subscription status strings
 *   - FREE_PLAN_FEATURES — fallback feature list shown while API loads
 *   - PLANS        — minimal fallback plan objects (free plan only)
 *
 * Behavior (upgrade/downgrade validation) lives in utils/planUtils.js, not here,
 * to separate policy from data (Rule of Separation).
 *
 * SYNC REQUIRED: PLAN_STATUS values are duplicated from `backend/constants/plans.js`
 * (canonical source is the PLAN_STATUS object there). These mirror Stripe subscription
 * statuses. To add or rename a value: update backend first, then update here.
 * Search for PLAN_STATUS usages in this repo to find all UI states that need updating.
 *
 * Plans are managed via the admin panel and stored in the database.
 * The backend is the source of truth for enforcement.
 */

/**
 * Plan subscription status values (matches backend PLAN_STATUS)
 */
export const PLAN_STATUS = {
  ACTIVE: "active",
  CANCELED: "canceled",
  PAST_DUE: "past_due",
  UNPAID: "unpaid",
  INCOMPLETE: "incomplete",
  INCOMPLETE_EXPIRED: "incomplete_expired",
  TRIALING: "trialing",
  PAUSED: "paused",
};

/**
 * Canonical feature list for the free plan fallback.
 *
 * Single source of truth for the JS side. The database stores the live
 * version; this list is only used while the API response is loading.
 *
 * SYNC: write the migration first (the database is the source of truth),
 * then add the feature here. The test in plans.test.js pins each feature
 * against the migration that added it — update that test too.
 */
export const FREE_PLAN_FEATURES = [
  "1 widget",
  "100 messages/month",
  "3 knowledge sources",
  "Email support",
];

/**
 * Minimal fallback plans — only the free plan.
 * The full plan list is fetched from the API (GET /api/billing/plans).
 * This prevents a blank UI while the API loads.
 *
 * INTENTIONALLY STALE: the limits below mirror the free plan row in the
 * database (see migration 20260207000000_create_plans_table.js) and are
 * shown only during the loading window before the API responds.  When
 * the free plan limits change in the DB, update the migration AND the
 * values here so the loading-state UI stays roughly accurate.
 */
const PLANS = [
  {
    id: "free",
    name: "Free",
    price: 0,
    limits: {
      widgets: 1, // matches DB: limit_widgets
      messagesPerMonth: 100, // matches DB: limit_messages_per_month
      knowledgeSources: 3, // matches DB: limit_knowledge_sources
    },
    features: FREE_PLAN_FEATURES,
  },
];

export default PLANS;
